import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLines } from "@/components/site/Reveal";
import { openWhatsApp, scrollToId } from "@/lib/api";

const HERO_IMG =
  "https://images.pexels.com/photos/7267081/pexels-photo-7267081.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1600";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden" data-testid="hero-section">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img src={HERO_IMG} alt="Hand-poured candle glowing in the dark" className="w-full h-[115%] object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0C0A09]/60 via-[#0C0A09]/45 to-[#0C0A09]" />
      </motion.div>

      <motion.div
        style={{ opacity }}
        className="relative z-10 max-w-7xl mx-auto px-6 min-h-screen flex flex-col justify-center pt-24"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 1 }}
          className="flex items-center gap-3 mb-8"
        >
          <span className="h-px w-10 bg-[#C4A47C]" />
          <span className="label text-[#C4A47C]">Howrah, West Bengal · 5.0 ★ on Google</span>
        </motion.div>

        <h1 className="font-serif font-light text-[#F5F5F0] tracking-tighter text-[3.25rem] leading-[0.95] sm:text-7xl lg:text-[7.5rem]">
          <MaskedLines
            lines={["Hand-poured", "with love,"]}
            delay={0.25}
          />
          <MaskedLines
            lines={["made to glow."]}
            delay={0.55}
            className="italic text-[#C4A47C]"
          />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1 }}
          className="mt-10 max-w-xl text-[#A8A29E] text-base sm:text-lg font-light leading-relaxed"
        >
          Thoughtfully crafted candles, floral bouquets & hand-cast figurines — made with
          high-quality ingredients, beautiful fragrances and a little artistry, to brighten
          your home and your heart.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-12 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("collection")}
            className="rounded-full bg-[#C4A47C] hover:bg-[#D8BC97] text-[#0C0A09] px-8 py-4 text-sm font-medium transition-colors"
            data-testid="hero-explore-btn"
          >
            Explore the collection
          </button>
          <button
            onClick={() => openWhatsApp("Hi Ave_luxe_candles! I'd love to place an order.")}
            className="rounded-full border border-[#292524] hover:border-[#C4A47C] text-[#F5F5F0] px-8 py-4 text-sm font-medium transition-colors"
            data-testid="hero-whatsapp-btn"
          >
            Order on WhatsApp
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#A8A29E]"
      >
        <motion.div animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <ArrowDown size={20} strokeWidth={1.2} />
        </motion.div>
      </motion.div>
    </section>
  );
}
